import {
  Link,
  OverflowTooltip,
  Table,
  TableColumn,
} from '@backstage/core-components';
import React, { useState } from 'react';
import { CatalogAutocomplete } from '@backstage/plugin-catalog-react';
import Checkbox from '@material-ui/core/Checkbox';
import { ExternalLink } from '@internal/ui-common';
import { Grid } from '@material-ui/core';
import ListItemText from '@material-ui/core/ListItemText';
import type { IMessage } from '../../schemas/IMessage.interface';
import { MessageStatus } from '../../enums/MessageStatus.enum';
import SearchIcon from '@material-ui/icons/Search';
import { SupportHeader } from '../supportHeader/SupportHeader';
import type { SvgIconProps } from '@material-ui/core';
import { messageDetailSubRouteRef } from '../../routes';

interface MessagesListProps {
  messages: IMessage[];
  isLoading: boolean;
  error: Error | null;
}

const search = React.forwardRef<SVGSVGElement, SvgIconProps>((props, ref) => (
  <SearchIcon {...props} ref={ref} />
));

function MessagesList({ messages, isLoading, error }: MessagesListProps) {
  const [selectedStatuses, setSelectedStatuses] = useState<MessageStatus[]>([
    MessageStatus.APPROVED,
    MessageStatus.PROPOSED,
  ]);
  const allStatuses = Object.values(MessageStatus);

  const filteredData =
    selectedStatuses.length === 0
      ? messages
      : messages.filter(row => new Set(selectedStatuses).has(row.status));

  // useRouteRef cannot be used here because routing is managed custom within this plugin - Not using Backstage's standard route matching
  // The plugin is not rendered as a full, real plugin because the subpages need to not inherit the parent plugin's/core's Headers and Navigation tabs, but the main page should inherit these
  function getMessageDetailPath(messageName: IMessage['name']): string {
    const path = messageDetailSubRouteRef.path.replace(':messageName', messageName);
    return path.replace(/^\//, '');
  }

  const columns: TableColumn<IMessage>[] = [
    {
      title: 'Name',
      field: 'name',
      highlight: true,
      defaultSort: 'asc',
      width: '20%',
      render: row => (
        <Link to={`${getMessageDetailPath(row.name)}`}>
          {row.name}
        </Link>
      ),
    },
    {
      title: 'Description',
      field: 'description',
      width: '50%',
      sorting: false,
      render: (row: any) =>
        row.description ? <OverflowTooltip text={row.description} /> : null,
    },
    { title: 'Type', 
      field: 'type', 
      width: '10%', 
      searchable: false },
    {
      title: 'Status',
      field: 'status',
      width: '10%',
      searchable: false,
      render: row => row.status,
    },
    {
      title: 'External docs',
      field: 'externalDocs',
      sorting: false,
      searchable: false,
      width: '10%',
      align: 'center',
      render: row => {
        if (!row.confluenceUrl) return null;

        return (
          <ExternalLink
            to={row.confluenceUrl}
            children="Confluence docs"
          />
        );
      },
    },
  ];

  return (
    <>
      <SupportHeader />
      <Grid container spacing={2}>
        <Grid item xs={12} lg={2}>
          <CatalogAutocomplete<MessageStatus, true>
            name="message-status-filter"
            label="Status"
            multiple
            options={allStatuses}
            value={selectedStatuses}
            onChange={(_, value) => setSelectedStatuses(value)}
            disableCloseOnSelect
            renderOption={(option, { selected }) => (
              <>
                <Checkbox checked={selected} />
                <ListItemText primary={String(option)} />
              </>
            )}
          />
        </Grid>

        <Grid item xs={12} lg={10}>
          <Table
            title="Messages"
            columns={columns}
            data={filteredData}
            isLoading={isLoading}
            icons={{
              ...Table.icons,
              Search: search,
            }}
            options={{
              search: true,
              pageSize: 20,
              paginationType: 'normal',
              pageSizeOptions: [20, 50, 100],
              padding: 'dense',
              headerStyle: { position: 'sticky' },
              draggable: false,
              thirdSortClick: false,
            }}
            {...(error && { error: error.message })}
          />
        </Grid>
      </Grid>
    </>
  );
}

export { MessagesList };
